import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "./ui/button";

const links = [
  { href: "#top", label: "Home" },
  { href: "#features", label: "Features" },
  { href: "#industries", label: "Business Types" },
  { href: "#pricing", label: "Pricing" },
  { href: "#about", label: "About Us" },
  { href: "#contact", label: "Support" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("#top");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-sm"
          : "bg-white/70 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-20 flex items-center justify-between">
          <a href="#top" data-testid="brand-logo" className="flex items-center group">
            <img
              src="/fyzeron-logo-dark.png"
              alt="Fyzeron Technologies LLP"
              className="h-9 sm:h-10 w-auto"
            />
          </a>

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) => {
              const isActive = active === l.href;
              return (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setActive(l.href)}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`relative text-[15px] font-semibold transition-colors ${
                    isActive ? "text-navy" : "text-slate-600 hover:text-navy"
                  }`}
                >
                  {l.label}
                  {isActive && (
                    <span className="absolute left-1/2 -translate-x-1/2 -bottom-2 h-[3px] w-7 rounded-full bg-navy" />
                  )}
                </a>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center">
            <a href="#contact">
              <Button
                data-testid="nav-book-demo"
                className="rounded-xl bg-navy hover:bg-navy/90 text-white px-5 h-11 font-semibold shadow-md shadow-navy/20"
              >
                Book a demo
              </Button>
            </a>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden p-2 rounded-lg text-navy hover:bg-slate-100"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden pb-4 pt-2 border-t border-slate-200" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="px-2 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg"
                >
                  {l.label}
                </a>
              ))}
              <a href="#contact" onClick={() => setOpen(false)} className="mt-2">
                <Button className="w-full rounded-xl bg-navy hover:bg-navy/90 text-white font-semibold">
                  Book a demo
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
